// merge-claude-md-managed-block.ts
//
// Merge "managed block" của Avatar vào CLAUDE.md hiện có khi `avatar sync`.
// Mục tiêu: cập nhật phần Avatar-quản (Workflows&Rules, hướng dẫn pack...) mà
// KHÔNG mất context user tự thêm.
//
// Cơ chế marker:
//   <!-- AVATAR:MANAGED:START ... -->  ... block do avatar quản ...  <!-- AVATAR:MANAGED:END ... -->
//
// 3 trường hợp:
//   1. CLAUDE.md có đủ cặp marker  → thay nội dung GIỮA marker bằng block mới,
//      giữ nguyên mọi thứ ngoài marker (phần user tự thêm).
//   2. CLAUDE.md KHÔNG có marker (file cũ render từ CLI trước)  → THAY HOÀN TOÀN:
//      xóa sạch nội dung cũ, ghi block Avatar mới. (Quyết định: CLAUDE.md là file
//      Avatar-managed; nội dung cũ không marker coi như không được bảo toàn.
//      Caller KHÔNG backup theo yêu cầu.)
//   3. Không truyền currentContent (file chưa tồn tại) → trả thẳng newManaged.
//
// Pure function, no-IO → dễ test. Caller lo đọc/ghi file.

export const MANAGED_START_PREFIX = "<!-- AVATAR:MANAGED:START";
export const MANAGED_END_PREFIX = "<!-- AVATAR:MANAGED:END";

export type MergeOutcome = "replaced-block" | "replaced-whole" | "created";

export interface MergeResult {
  content: string;
  outcome: MergeOutcome;
}

// Tìm vị trí dòng marker START/END (match theo prefix, không cần khớp đủ comment
// để chịu được khác biệt text sau prefix giữa các version).
function findMarkerLine(lines: string[], prefix: string): number {
  return lines.findIndex((l) => l.trimStart().startsWith(prefix));
}

/**
 * Merge block managed mới vào CLAUDE.md hiện có.
 *
 * @param currentContent nội dung CLAUDE.md hiện tại (undefined nếu file chưa có)
 * @param newManaged     block managed mới render từ template — PHẢI chứa cặp marker
 */
export function mergeClaudeMdManagedBlock(
  currentContent: string | undefined,
  newManaged: string,
): MergeResult {
  // TH3: chưa có file → trả block mới nguyên vẹn.
  if (currentContent === undefined || currentContent.trim() === "") {
    return { content: newManaged, outcome: "created" };
  }

  const lines = currentContent.split("\n");
  const startIdx = findMarkerLine(lines, MANAGED_START_PREFIX);
  const endIdx = findMarkerLine(lines, MANAGED_END_PREFIX);

  // TH1: có đủ cặp marker hợp lệ (start trước end) → thay phần giữa.
  if (startIdx !== -1 && endIdx !== -1 && startIdx < endIdx) {
    const before = lines.slice(0, startIdx); // phần trên marker START (giữ nguyên)
    const after = lines.slice(endIdx + 1); // phần dưới marker END (user — giữ nguyên)
    const merged = [...before, newManaged.trimEnd(), ...after].join("\n");
    return { content: merged, outcome: "replaced-block" };
  }

  // TH2: không có marker (file cũ render từ CLI trước) → THAY HOÀN TOÀN.
  // Xóa sạch nội dung cũ, ghi block Avatar mới (có marker để lần sau nhận diện).
  // KHÔNG giữ nội dung cũ, KHÔNG backup (theo yêu cầu — caller không backup).
  return { content: newManaged, outcome: "replaced-whole" };
}
